import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';
import { User, AuthUser, LoginDto, RegisterDto } from '../../../domain/entities';
import { authApi } from '../../../data/api';
import { AUTH_TOKEN_KEY, USER_KEY } from '../../../core/config/constants';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: JSON.parse(localStorage.getItem(USER_KEY) || 'null'),
  token: Cookies.get(AUTH_TOKEN_KEY) || null,
  isAuthenticated: !!Cookies.get(AUTH_TOKEN_KEY),
  isLoading: false,
  error: null,
};

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginDto) => {
    const response = await authApi.login(credentials);
    return response;
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (userData: RegisterDto) => {
    const response = await authApi.register(userData);
    return response;
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async () => {
    await authApi.logout();
  }
);

export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async () => {
    const response = await authApi.getCurrentUser();
    console.log('🔍 API /user/check - Réponse complète:', response);
    console.log('🔍 Type de réponse:', typeof response);
    console.log('🔍 Clés disponibles:', response ? Object.keys(response) : 'Aucune');
    if (response && typeof response === 'object') {
      console.log('🔍 Structure détaillée:', JSON.stringify(response, null, 2));
    }
    return response;
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    logoutLocal: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
      Cookies.remove(AUTH_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<AuthUser>) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
        state.token = action.payload.token;
        Cookies.set(AUTH_TOKEN_KEY, action.payload.token);
        localStorage.setItem(USER_KEY, JSON.stringify(action.payload));
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Email ou mot de passe incorrect';
      })
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action: PayloadAction<AuthUser>) => {
        state.isLoading = false;
        // Ne pas connecter automatiquement après l'inscription
        // L'utilisateur devra se connecter manuellement
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Erreur lors de l\'inscription';
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.error = null;
        Cookies.remove(AUTH_TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action: PayloadAction<User>) => {
        state.user = action.payload;
        localStorage.setItem(USER_KEY, JSON.stringify(action.payload));
      });
  },
});

export const { clearError, logoutLocal } = authSlice.actions;
export default authSlice.reducer;